import React from 'react';
import RangeSliderRefinement from './RangeSliderRefinement/RangeSliderRefinement';
import { CheckboxToggleRefinement } from './CheckboxToggleRefinement/CheckboxToggleRefinement';
import DoubleRangeSlider from '../../../components/double-range-slider/DoubleRangeSlider';
import { styles } from './RangeSliderRefinement/RangeSliderRefinement.css';
import { useGoogleSearchContext } from '../../../components/search/GoogleInstantSearch';

type GoogleRangeSliderRefinementProps = {
  facet: string;
  setDisableScroll: (v: boolean) => void;
  onDropdownToggle?: (isOpen: boolean) => void;
  isSearchPage?: boolean;
};

const GoogleRangeSliderRefinement = ({
  facet,
  setDisableScroll,
  onDropdownToggle,
  isSearchPage,
}: GoogleRangeSliderRefinementProps) => {
  const { nbHits, facets, facetsStats, state, setPriceRange, clearPriceRange, setSaleItems, setClearanceItems, setRedHotDealItems } =
    useGoogleSearchContext();

  const priceStats = facetsStats['Price'] ?? facetsStats['facets.Price'];
  const min = priceStats?.min ?? 0;
  const max = priceStats?.max ?? 0;

  const activePriceFacet = state.facets.find(f => f.name === 'Price' && Array.isArray(f.value));
  const [currentMin, currentMax] = Array.isArray(activePriceFacet?.value) ? activePriceFacet.value : [min, max];

  const priceIsRefined = !!activePriceFacet && (currentMin !== min || currentMax !== max);
  const isRefined = priceIsRefined || state.isSaleItems || state.isClearanceItems || state.isRedHotDealItems;

  const onPromoFacet = facets['onPromotion'] ?? {};
  const nbSaleItems = onPromoFacet['true'] ?? onPromoFacet['True'] ?? onPromoFacet['1'] ?? 0;
  const nbClearanceItems = facets['allCalculatedProductTags']?.['Clearance'] ?? 0;
  const nbRedHotDealItems = facets['allCalculatedProductTags']?.['Red Hot Deal'] ?? 0;

  const handleRefine = ({ currentMin: newMin, currentMax: newMax }: { currentMin: number; currentMax: number }) => {
    if (newMin <= min && newMax >= max) {
      clearPriceRange();
    } else {
      setPriceRange(newMin, newMax);
    }
  };

  return (
    <RangeSliderRefinement
      attribute="Price"
      facet={facet}
      nbHits={nbHits}
      defaultRefinement={{ min, max }}
      setDisableScroll={setDisableScroll}
      trackingInfo={{ position: 1, defaults: { hitsPerPage: 36, sortBy: 'relevancy' } }}
      onDropdownToggle={onDropdownToggle}
      nbSaleItems={nbSaleItems}
      nbClearanceItems={nbClearanceItems}
      isSearchPage={isSearchPage}
      isRefined={isRefined}
    >
      <div className="resultNum">
        {nbHits} result{nbHits !== 1 ? 's' : ''}
      </div>
      <DoubleRangeSlider
        min={min}
        max={max}
        currentMin={currentMin}
        currentMax={currentMax}
        nbHits={nbHits}
        showDollar={facet === 'Price'}
        onRefine={handleRefine}
      />
      {facet === 'Price' && (
        <div className={styles.checkboxToggleContainer}>
          {nbSaleItems > 0 && (
            <CheckboxToggleRefinement
              label="Sale items"
              isChecked={state.isSaleItems}
              onChange={e => setSaleItems(e.target.checked)}
              count={nbSaleItems || undefined}
            id="googleSaleItemsRefinement"
            />
          )}
          {nbClearanceItems > 0 && (
            <CheckboxToggleRefinement
              label="Clearance"
              isChecked={state.isClearanceItems}
              onChange={e => setClearanceItems(e.target.checked)}
              count={nbClearanceItems || undefined}
              id="googleClearanceItemsRefinement"
            />
          )}
          {nbRedHotDealItems > 0 && (
            <CheckboxToggleRefinement
              label="Red hot deal"
              isChecked={state.isRedHotDealItems}
              onChange={e => setRedHotDealItems(e.target.checked)}
              count={nbRedHotDealItems || undefined}
              id="googleRedHotDealItemsRefinement"
            />
          )}
        </div>
      )}
    </RangeSliderRefinement>
  );
};

export default GoogleRangeSliderRefinement;
